// Gửi thông báo — Đại lý / CTV (docs/KE_HOACH_GUI_THONG_BAO.md §5): Đại lý gửi CTV / Shop trong nhánh (cả nhánh, theo
// nhánh CTV, từng đơn vị); CTV gửi Shop của mình. Nhân viên cần quyền "Gửi thông báo" (network.announce).
// API: apps/api /network/announcements; đơn vị để chọn: GET /network/units (cùng cache với trang Mạng lưới).

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { get, post } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { PageHeader } from '@/components/ui/page-header'
import { AnnounceComposer } from '@/components/announce/AnnounceComposer'
import { SentAnnouncements } from '@/components/announce/SentAnnouncements'
import type { AnnounceApi, AnnounceUnit, AnnounceUnitType } from '@/components/announce/types'

interface NetworkUnit {
  id: string
  name: string
  tenantType: AnnounceUnitType
  parId: string | null
  direct: boolean
  status: string
  owner: { name: string | null; phone: string | null } | null
}

const AGENCY_TYPES: AnnounceUnitType[] = ['ctv', 'shop']
const AGENCY_BRANCHES: AnnounceUnitType[] = ['ctv']
const CTV_TYPES: AnnounceUnitType[] = ['shop']
const NO_BRANCHES: AnnounceUnitType[] = []

export default function NetworkAnnouncementsPage() {
  const auth = useAuth()
  const me = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const isAgency = me?.tenantType === 'agency'

  const api = useMemo<AnnounceApi>(
    () => ({ area: `unit:${auth.activeShopId ?? ''}`, base: '/network/announcements', get, post }),
    [auth.activeShopId],
  )
  const unitsQ = useQuery({ queryKey: ['network-units'], queryFn: () => get<{ units: NetworkUnit[] }>('/network/units') })
  // Đơn vị đã xóa / tạm ngưng không nhận (máy chủ bỏ) — không cho chọn. Trực thuộc mình → gốc danh sách.
  const units = useMemo<AnnounceUnit[]>(
    () => (unitsQ.data?.units ?? [])
      .filter((u) => u.status !== 'deleted' && u.status !== 'suspended')
      .map((u) => ({ id: u.id, name: u.name, tenantType: u.tenantType, parId: u.direct ? null : u.parId, owner: u.owner })),
    [unitsQ.data],
  )

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader title="Gửi thông báo" actions={<Link to="/network" className="inline-flex min-h-11 items-center text-sm text-accent hover:underline desktop:min-h-0">← Hệ thống</Link>} />
      <AnnounceComposer
        key={auth.activeShopId ?? ''}
        api={api}
        types={isAgency ? AGENCY_TYPES : CTV_TYPES}
        branchTypes={isAgency ? AGENCY_BRANCHES : NO_BRANCHES}
        units={units}
        unitsLoading={unitsQ.isLoading}
        unitsError={unitsQ.isError}
        onRetryUnits={() => unitsQ.refetch()}
        senderName={me?.name ?? ''}
      />
      <SentAnnouncements api={api} />
    </div>
  )
}
